import type { QuestionBankItem } from '@/components/lessonBuilder/QuestionBankPicker'
import { ACTIVITY_TYPE_CONFIG } from '@/constants/activityTypes'

/**
 * Convert a QuestionBankItem (from the API) into the activity_data format
 * that gets stored on a LessonActivity.
 *
 * Common fields (question, question_id, options, correct_answer, hints,
 * explanation, category, difficulty) are top-level in the DB and become
 * top-level in activity_data.  Type-specific fields are spread in from
 * type_specific_data.
 */
export function questionBankItemToActivityData(q: QuestionBankItem): Record<string, any> {
  const data: Record<string, any> = {
    question: q.question,
    // Retain the source bank question's id. Required by activities whose
    // start validation demands question_id (e.g. phishing_quiz) and used by
    // editors to detect that a bank question has been loaded.
    question_id: q.id,
  }

  if (q.options != null) data.options = q.options
  if (q.correct_answer != null) data.correct_answer = q.correct_answer
  if (q.explanation) data.explanation = q.explanation
  if (q.hints && q.hints.length > 0) data.hints = q.hints
  if (q.category) data.category = q.category
  if (q.difficulty) data.difficulty = q.difficulty

  // Merge all type-specific fields (packets, test_cases, starter_code, etc.)
  if (q.type_specific_data) {
    Object.assign(data, q.type_specific_data)
  }

  return data
}

/**
 * Derive an activity title from a QuestionBankItem.
 * Uses category if available, otherwise truncates the question.
 */
export function questionBankItemTitle(q: QuestionBankItem): string {
  if (q.category) return q.category
  const text = q.question || ''
  return text.length > 60 ? text.slice(0, 57) + '...' : text
}

/**
 * Get the default time limit for a question bank item's activity type.
 */
export function questionBankItemTimeLimit(q: QuestionBankItem): number | null {
  return ACTIVITY_TYPE_CONFIG[q.activity_type]?.defaultTimeLimit ?? null
}

/**
 * Build the full attributes object needed by onReplaceWithQuestion / bulk-add.
 */
export function questionBankItemToActivityAttrs(q: QuestionBankItem): {
  activity_type: string
  title: string
  instruction: string
  activity_data: Record<string, any>
  time_limit_seconds: number | null
} {
  return {
    activity_type: q.activity_type,
    title: questionBankItemTitle(q),
    instruction: '',
    activity_data: questionBankItemToActivityData(q),
    time_limit_seconds: questionBankItemTimeLimit(q),
  }
}
